import { defineStore } from 'pinia';
import type { CnaeList, CnaeListCreate, CnaeListUpdate } from '@/types/cnaeList';
import { ref, computed } from 'vue';
import { useRiskProfileOptions } from '@/composables/useRiskProfileOptions';

interface CnaeListApi {
    getCnaeLists(): Promise<CnaeList[]>;
    createCnaeList(newList: CnaeListCreate): Promise<CnaeList>;
    updateCnaeList(id: string, updates: CnaeListUpdate): Promise<CnaeList>;
    deleteCnaeList(id: string): Promise<void>;
    addCnaesToList(listId: string, cnaeCodes: string[]): Promise<CnaeList>;
    removeCnaesFromList(listId: string, cnaeItemIds: string[]): Promise<CnaeList>;
}

export function createCnaeListStore(storeId: string, api: CnaeListApi) {
    return defineStore(storeId, () => {
        const cnaeLists = ref<CnaeList[]>([]);
        const isLoading = ref(false);
        const error = ref<string | null>(null);
        const { fetchOptions: fetchRiskModelOptions } = useRiskProfileOptions();

        const getCnaeListById = computed(() => {
            return (id: string) => cnaeLists.value.find(list => list.id === id);
        });

        async function fetchCnaeLists() {
            isLoading.value = true;
            error.value = null;
            try {
                cnaeLists.value = await api.getCnaeLists();
            } catch (e: any) {
                error.value = e.message || `Failed to fetch CNAE lists for ${storeId}`;
            } finally {
                isLoading.value = false;
            }
        }

        async function createCnaeList(newList: CnaeListCreate) {
            isLoading.value = true;
            error.value = null;
            try {
                const createdList = await api.createCnaeList(newList);
                cnaeLists.value.push(createdList);
                await fetchRiskModelOptions(true);
            } catch (e: any) {
                error.value = e.message || `Failed to create CNAE list for ${storeId}`;
            } finally {
                isLoading.value = false;
            }
        }

        async function updateCnaeList(id: string, updates: CnaeListUpdate) {
            isLoading.value = true;
            error.value = null;
            try {
                const updatedList = await api.updateCnaeList(id, updates);
                const index = cnaeLists.value.findIndex(list => list.id === id);
                if (index !== -1) {
                    cnaeLists.value[index] = updatedList;
                }
                await fetchRiskModelOptions(true);
            } catch (e: any) {
                error.value = e.message || `Failed to update CNAE list for ${storeId}`;
            } finally {
                isLoading.value = false;
            }
        }

        async function deleteCnaeList(id: string) {
            isLoading.value = true;
            error.value = null;
            try {
                await api.deleteCnaeList(id);
                cnaeLists.value = cnaeLists.value.filter(list => list.id !== id);
                await fetchRiskModelOptions(true);
            } catch (e: any) {
                error.value = e.message || `Failed to delete CNAE list for ${storeId}`;
            } finally {
                isLoading.value = false;
            }
        }

        async function addCnaesToList(listId: string, cnaeCodes: string[]) {
            isLoading.value = true;
            error.value = null;
            try {
                const updatedList = await api.addCnaesToList(listId, cnaeCodes);
                const index = cnaeLists.value.findIndex(list => list.id === listId);
                if (index !== -1) {
                    cnaeLists.value[index] = updatedList;
                }
                await fetchRiskModelOptions(true);
            } catch (e: any) {
                error.value = e.message || `Failed to add CNAEs to list for ${storeId}`;
            } finally {
                isLoading.value = false;
            }
        }

        async function removeCnaesFromList(listId: string, cnaeItemIds: string[]) {
            isLoading.value = true;
            error.value = null;
            try {
                const updatedList = await api.removeCnaesFromList(listId, cnaeItemIds);
                const index = cnaeLists.value.findIndex(list => list.id === listId);
                if (index !== -1) {
                    cnaeLists.value[index] = updatedList;
                }
                await fetchRiskModelOptions(true);
            } catch (e: any) {
                error.value = e.message || `Failed to remove CNAEs from list for ${storeId}`;
            } finally {
                isLoading.value = false;
            }
        }

        return {
            cnaeLists,
            isLoading,
            error,
            getCnaeListById,
            fetchCnaeLists,
            createCnaeList,
            updateCnaeList,
            deleteCnaeList,
            addCnaesToList,
            removeCnaesFromList,
        };
    });
}
